import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { FlaskConical, ChevronRight, Users, MessageSquare, Milestone } from 'lucide-react';

const BACKEND_URL = '';

/**
 * MostActiveBriteLabsCard — right-rail card listing the top 3 BriteLabs
 * ranked by combined activity (messages + milestones) in the last 90 days.
 * Backed by GET /api/britelabs/most-active.
 */
export default function MostActiveBriteLabsCard({ limit = 3, testid = 'most-active-britelabs' }) {
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/britelabs/most-active?limit=${limit}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : { labs: [] })
      .then(d => setLabs(Array.isArray(d?.labs) ? d.labs : []))
      .catch(() => setLabs([]))
      .finally(() => setLoading(false));
  }, [limit]);

  if (!loading && labs.length === 0) return null;

  return (
    <Card className="p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FlaskConical className="h-4 w-4 text-brand-sage" />
          <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wide">Most Active BriteLabs</h3>
        </div>
        <Link to="/britelabs"
          className="text-[11px] text-brand-navy hover:underline inline-flex items-center gap-0.5"
          data-testid={`${testid}-view-all`}>
          View all <ChevronRight className="h-3 w-3" />
        </Link>
      </div>

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: limit }).map((_, i) => (
            <div key={i} className="animate-pulse space-y-1.5">
              <div className="h-3 bg-slate-100 rounded w-3/4" />
              <div className="h-2 bg-slate-100 rounded w-1/2" />
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {labs.map((l, i) => (
            <Link
              key={l.lab_id}
              to={`/britelabs/${l.lab_id}`}
              className="block group"
              data-testid={`${testid}-item-${l.lab_id}`}
            >
              <div className="flex gap-2 items-start">
                <span className="text-[11px] font-bold text-brand-sage w-4 flex-shrink-0 pt-0.5">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-slate-800 line-clamp-2 leading-snug group-hover:text-brand-navy transition-colors">
                    {l.title}
                  </p>
                  <div className="flex items-center gap-2 mt-1 text-[10px] text-slate-400">
                    {l.category && <span className="uppercase tracking-wide">{l.category}</span>}
                    <span className="inline-flex items-center gap-0.5"><Users className="h-2.5 w-2.5" />{l.stakeholder_count}</span>
                    {l.message_count > 0 && (
                      <span className="inline-flex items-center gap-0.5"><MessageSquare className="h-2.5 w-2.5" />{l.message_count}</span>
                    )}
                    {l.milestone_count > 0 && (
                      <span className="inline-flex items-center gap-0.5"><Milestone className="h-2.5 w-2.5" />{l.milestone_count}</span>
                    )}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </Card>
  );
}
